import type { Logger } from "@expert/logging";
import type { CallCategory } from "@expert/shared-types";
import type { OpenGuideSource } from "@expert/solve-tools";

import { addGuideRecommendationMessage } from "@asurion-core/expert-workspace-timeline";
import { useFeatures, type WorkspaceFeatures } from "@expert/features";
import { trackFullStoryEvent } from "@expert/monitoring";
import {
    clearRecommendation,
    guidedFlowRecommendationMap,
    isGuideRecommendationDisabled,
    isGuideSupported,
    useGuideSession,
} from "@expert/solve-tools";
import { useEffect, useMemo } from "react";

import type { usePartner } from "@/sdk";

import { getRootDispatcher } from "@/analytics";

import { DEVICE_ACTIVATION_GUIDE, getActivationGuideTitle } from "../device-activation/shouldOpenActivationGuide";

export const useGuideRecommendation = ({
    callSid,
    isGuideOpen,
    logger,
    openGuide,
    partner,
    sessionId,
}: {
    callSid?: string;
    isGuideOpen: boolean;
    logger: Logger;
    openGuide: (source: OpenGuideSource, guide?: string) => void;
    partner: ReturnType<typeof usePartner>;
    sessionId: string;
}) => {
    const rootDispatcher = getRootDispatcher();

    const enhancedDispatcher = useMemo(
        () => (callSid ? rootDispatcher.withIdentities({ CallSid: callSid }) : rootDispatcher),
        [callSid, rootDispatcher],
    );

    const {
        features: { isGuidedFlowRecommendationEnabled, isVerizonDeviceActivationApiEnabled },
    } = useFeatures<WorkspaceFeatures>();

    const { analyticsInfo, disableAutomaticOpen, recommendation } = useGuideSession(sessionId);

    useEffect(() => {
        if (!isGuidedFlowRecommendationEnabled) return;
        if (!recommendation) return;

        const mappedGuide = guidedFlowRecommendationMap[recommendation as CallCategory];
        // Same title choice as the intent / queue / TFN auto-open, so a flagged Verizon expert gets the API guide
        // whichever path opens it.
        const guideToAutoOpen =
            mappedGuide === DEVICE_ACTIVATION_GUIDE
                ? getActivationGuideTitle({ isVerizonDeviceActivationApiEnabled, partner, sessionId })
                : mappedGuide;
        if (!guideToAutoOpen) {
            logger.info({ recommendation }, "Solve Guide | unknown recommendation, clearing");
            clearRecommendation(logger, sessionId);
            return;
        }

        if (isGuideOpen || disableAutomaticOpen) {
            logger.info(
                { disableAutomaticOpen, isGuideOpen, recommendation },
                "Solve Guide | recommendation received, but not opening guide",
            );
            clearRecommendation(logger, sessionId);
            return;
        }

        // Defer until the Simplr session has loaded the GuideSelection step. Opening sooner produces
        // an empty panel because availableGuides may be stale-from-persist while the new session's
        // messages haven't loaded yet, and automaticallySelectGuideIfNeeded silently no-ops unless
        // currentStepId === "GuideSelection".
        if (analyticsInfo.currentStepId !== "GuideSelection") {
            logger.info(
                { currentStepId: analyticsInfo.currentStepId, recommendation },
                "Solve Guide | recommendation pending Simplr session readiness",
            );
            return;
        }

        const guideSupported = isGuideSupported(sessionId, guideToAutoOpen);
        const guideDisabled = isGuideRecommendationDisabled(sessionId, guideToAutoOpen);
        if (!guideSupported || guideDisabled) {
            logger.info(
                { isGuideRecommendationDisabled: guideDisabled, isGuideSupported: guideSupported, recommendation },
                "Solve Guide | not opening guide - either not supported or disabled",
            );
            clearRecommendation(logger, sessionId);
            return;
        }

        logger.info({ recommendation }, "Solve Guide | auto opening guide for recommendation");
        void enhancedDispatcher.dispatchBusinessEvent("Guide_RecommendationMessageShown", {
            guide: guideToAutoOpen,
        });
        trackFullStoryEvent("Guide_RecommendationMessageShown", { guide: guideToAutoOpen });
        addGuideRecommendationMessage(guideToAutoOpen, sessionId);
        openGuide({ source: "auto", sourceDetails: "recommendation" }, guideToAutoOpen);
        clearRecommendation(logger, sessionId);
    }, [
        analyticsInfo.currentStepId,
        disableAutomaticOpen,
        enhancedDispatcher,
        isGuideOpen,
        isGuidedFlowRecommendationEnabled,
        isVerizonDeviceActivationApiEnabled,
        logger,
        openGuide,
        partner,
        recommendation,
        sessionId,
    ]);
};
